<template>
  <div
    class="flex flex-col rounded-2 pa-4 base min-h-full h-full"
    :style="{
      border:
        isDragNDropEnabled && isDragging && !draggingOverItemId && !draggingOverItemSeparator
          ? `1px dashed ${themeVars.primary}`
          : '1px solid transparent',
    }"
    v-on="
      isDragNDropEnabled
        ? {
            dragenter: handleDragEnter,
            dragleave: handleDragLeave,
            dragover: handleDragOver,
            drop: handleDrop,
          }
        : {}
    "
  >
    <div class="flex justify-between items-center mb-4 sticky top-0 base z-1">
      <h2 class="panel-title font-600 font-size-[15px] line-height-[28px]">
        <template v-if="!appProps?.maxSelections?.value">{{
          t('canvasUI.contentSelectorApp.selectionPanel.selectedCount', {
            count: isDragNDropEnabled ? addedItems.length : selectedItems.length,
          })
        }}</template>
        <template v-else>{{
          t('canvasUI.contentSelectorApp.selectionPanel.selectedCountMax', {
            count: isDragNDropEnabled ? addedItems.length : selectedItems.length,
            max: appProps?.maxSelections.value,
          })
        }}</template>
      </h2>
      <CButton
        class="pa-1 pl-2 !normal-case !tracking-normal underline"
        :disabled="isDragNDropEnabled ? !addedItems.length : !selectedItems.length"
        size="tiny"
        text
        @click="isDragNDropEnabled ? resetAddedItems() : resetSelectedItems()"
      >
        {{ t('canvasUI.contentSelectorApp.selectionPanel.clearSelection') }}
      </CButton>
    </div>

    <Draggable
      v-if="isDragNDropEnabled ? addedItems.length : selectedItems.length"
      v-model="draggableItems"
      handle=".selection-panel-list-handle"
      item-key="id"
    >
      <div v-for="(item, index) in isDragNDropEnabled ? addedItems : selectedItems" :key="item.id">
        <div
          v-if="isDragNDropEnabled && index > 0"
          class="flex items-center w-full h-2"
          v-on="
            isDragNDropEnabled
              ? {
                  dragend: clearState,
                  dragover: (event: DragEvent) => {
                    event.stopPropagation()
                    handleDragOver(event, item.id, true)
                  },
                  drop: (event: DragEvent) => {
                    event.stopPropagation()
                    handleDrop(event, item.id, index)
                  },
                  mouseleave: clearState,
                }
              : {}
          "
        >
          <div
            class="w-full"
            :style="{
              borderBottom:
                isDragNDropEnabled && !draggingOverItemId && draggingOverItemSeparator === item.id
                  ? `1px dashed ${themeVars.primary}`
                  : '1px solid transparent',
            }"
          />
        </div>

        <div
          class="selection-panel-list-item flex items-center h-14 rounded-2"
          :class="{ 'mt-2': !!index && !isDragNDropEnabled }"
          :style="{
            border:
              isDragNDropEnabled && !draggingOverItemSeparator && draggingOverItemId === item.id
                ? `1px dashed ${themeVars.primary}`
                : '1px solid transparent',
          }"
          v-on="
            isDragNDropEnabled
              ? {
                  dragend: clearState,
                  dragover: (event: DragEvent) => {
                    event.stopPropagation()
                    handleDragOver(event, item.id)
                  },
                  drop: (event: DragEvent) => {
                    event.stopPropagation()
                    handleDrop(event, item.id, index)
                  },
                  mouseleave: clearState,
                }
              : {}
          "
        >
          <CIcon
            class="selection-panel-list-handle px-1 cursor-move flex-shrink-0"
            fa-type="fas"
            icon="grip-dots-vertical"
            size="20px"
          />

          <div
            v-if="item.type === 'file'"
            class="w-16 h-10 flex flex-shrink-0 items-center justify-center cursor-pointer rounded-2 overflow-hidden"
            @click.stop="previewFile(item, $event)"
          >
            <img
              v-if="item.thumbnail_url"
              :alt="item.name ? item.name : 'File thumbnail'"
              class="w-full h-full object-cover"
              :src="item.thumbnail_url"
              @error="handleThumbnailError($event, item)"
            />
            <img
              v-else-if="getDefaultThumbnailUrl(item as any)"
              :alt="item.name ? item.name : 'File thumbnail'"
              class="w-full h-full object-cover"
              :src="getDefaultThumbnailUrl(item as any) || undefined"
            />
            <div v-else class="bg-primary6 w-full h-full flex items-center justify-center">
              <CIcon :color="themeVars.primary3" icon="file" size="24px" />
            </div>
          </div>
          <CFileThumbnail
            v-else
            class="w-16 h-10 flex flex-shrink-0 items-center justify-center cursor-pointer"
            :height="40"
            :icon-size="24"
            :item="
              isExternalLink(item)
                ? (getParsedExternalLinkItem(item as SelectedExternalLink) as unknown as RenderableFile)
                : (item as unknown as RenderableFile)
            "
            preview
            :width="60"
          />

          <div v-if="isFile(item)" class="flex-grow flex flex-col gap-1 mx-2 min-w-0 w-full leading-none">
            <div class="title text-ellipsis whitespace-nowrap font-700 overflow-hidden">
              {{ item.name }}
            </div>
            <div class="text-ellipsis whitespace-nowrap color-text2 truncate font-size-[12px]">
              <CIcon
                v-if="isFileExpired(item.expires_at || '') && isAdmin"
                v-tooltip="
                  t('canvasUI.files.expiredTooltip', {
                    time: dayjsInstance(item.expires_at).format('HH:mm'),
                    date: dayjsInstance(item.expires_at).format('LL'),
                  })
                "
                class="mr-2"
                :color="themeVars.error"
                fa-type="fa-kit"
                icon="regular-clock-circle-exclamation"
              />
              <CIcon
                v-if="isFileExpiring(item.expires_at || '') && !isFileExpired(item.expires_at || '') && isAdmin"
                v-tooltip="
                  t('canvasUI.files.expiringTooltip', {
                    time: dayjsInstance(item.expires_at).format('HH:mm'),
                    date: dayjsInstance(item.expires_at).format('LL'),
                  })
                "
                class="mr-2"
                :color="themeVars.warning3"
                fa-type="fa-kit"
                icon="regular-clock-circle-exclamation"
              />
              <component :is="renderContentType(item.content_type)" class="color-primary3" />
            </div>
          </div>

          <div v-else-if="isPage(item)" class="flex-grow flex flex-col gap-1 mx-2 min-w-0 w-full leading-none">
            <div class="title text-ellipsis whitespace-nowrap font-700 overflow-hidden">
              {{ t('canvasUI.contentSelectorApp.selectionPanel.pageName', { number: item.page_index + 1 }) }}
            </div>
            <div class="description text-ellipsis whitespace-nowrap overflow-hidden">
              {{ item.file.name }}
            </div>
          </div>
          <div v-else-if="isAsset(item)" class="flex-grow flex flex-col mx-2 min-w-0 w-full overflow-hidden">
            <div class="title text-ellipsis whitespace-nowrap font-700">
              {{ (item as SelectedAsset).file?.name }}
            </div>
            <div class="description text-ellipsis whitespace-nowrap">
              {{ (item as SelectedAsset).content_extension?.toUpperCase() }}
            </div>
          </div>
          <div v-else-if="isExternalLink(item)" class="flex-grow flex flex-col mx-2 min-w-0 w-full overflow-hidden">
            <div class="title text-ellipsis whitespace-nowrap font-700">
              {{ getExternalLinkTitle(item as SelectedExternalLink) }}
            </div>
            <div class="description text-ellipsis whitespace-nowrap">
              {{ (item as SelectedExternalLink).content_type?.toUpperCase() }}
            </div>
          </div>
          <CButton
            circle
            class="mr-2 flex-shrink-0"
            :color="themeVars.primary5"
            :text-color="themeVars.primary"
            @click="removeItem(item as any)"
          >
            <CIcon icon="close" size="16px" />
          </CButton>
        </div>
      </div>
    </Draggable>
    <div v-else class="w-full h-full flex flex-col gap-4 items-center justify-center color-primary3">
      <CIcon icon="face-meh-blank" :size="32" />
      <span class="text-size-xs">{{ t('canvasUI.contentSelectorApp.selectionPanel.nothingSelectedYet') }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, type ToRefs, computed, type ComputedRef, ref, onUnmounted } from 'vue'
import { VueDraggable as Draggable } from 'vue-draggable-plus'
import { useI18n } from 'vue-i18n'
import { getFileId } from '@content-selector/util/file'
import { isModifierClick } from '@lib/util/navigation.util'
import dayjsInstance from 'dayjs'
import { renderTemplate } from '@lib/util/handlebars'
import useCrmShapeWithSectionContext from '@canvas-builder/composables/useCrmShapeWithSectionContext'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'

import CIcon from '@lib/components/CIcon/CIcon.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import { useStore, useStoreApi } from '@content-selector/composables/useStore'

import {
  isPage,
  isFile,
  isAsset,
  isExternalLink,
  type SelectedFile,
  type SelectedPage,
  type SelectedAsset,
  type SelectedExternalLink,
} from '@content-selector/stores/app'
import useFileDisplayHelpers from '@lib/composables/useFileDisplayHelpers'
import useThemeVars from '@lib/composables/useThemeVars'
import type { AppProps } from '@content-selector/appTypes'
import CFileThumbnail from '@lib/components/CFileThumbnail/CFileThumbnail.vue'
import type { RenderableFile } from '@lib/types/files'
import { getDefaultThumbnailUrl } from '@lib/utils/defaultThumbnail.util'
import type { Canvas } from '@openapi'

const { t } = useI18n()
const { renderContentType, isFileExpired, isFileExpiring } = useFileDisplayHelpers()
const themeVars = useThemeVars()
const appProps = inject<ToRefs<AppProps>>('appProps')
const {
  addedItems,
  resetAddedItems,
  selectedItems,
  resetSelectedItems,
  deleteItem,
  deselectItem,
  isAdmin,
  addItems,
  isDragNDropEnabled,
} = useStore()
const { state: api } = useStoreApi()

// Inject LaunchDarkly flags for CRM shape feature detection
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

// Use composables for context building
const { fullContext } = useCrmShapeWithSectionContext()

// Check if CRM shape feature is enabled
const isCrmShapeEnabled = computed(() => launchDarkly.value?.enable_crm_shape_for_external_links ?? false)

const draggableItems = computed({
  get: () => (isDragNDropEnabled.value ? addedItems.value : selectedItems.value),
  set: (value) => {
    if (isDragNDropEnabled.value) {
      addedItems.value = value
    } else {
      selectedItems.value = value
    }
  },
})

// Parse external link URL with fallback logic
function getParsedUrl(url: string): string {
  if (!url || !isCrmShapeEnabled.value) return url

  try {
    return renderTemplate(url, fullContext.value)
  } catch (error) {
    console.warn('Failed to parse template in external URL, using original URL:', error)
    return url
  }
}

// Check if URL contains dynamic value templates
function hasDynamicValues(url: string): boolean {
  return url.includes('{{') && url.includes('}}')
}

// Get display title for external link
function getExternalLinkTitle(item: SelectedExternalLink): string {
  // If feature flag is enabled and URL has dynamic values, show the URL with templates
  if (isCrmShapeEnabled.value && hasDynamicValues(item.url)) {
    return item.url
  }

  // Otherwise show the standard translation
  return item.from === 'ai'
    ? t('canvasUI.contentSelectorApp.selectionPanel.externalLinkAi')
    : t('canvasUI.contentSelectorApp.selectionPanel.externalLink')
}

// Create parsed version of external link items for thumbnail display
function getParsedExternalLinkItem(item: SelectedExternalLink): SelectedExternalLink {
  if (!isCrmShapeEnabled.value || !item.thumbnail_url) {
    return item
  }

  return {
    ...item,
    thumbnail_url: getParsedUrl(item.thumbnail_url),
  }
}

function handleThumbnailError(event: Event, item: any) {
  const img = event.target as HTMLImageElement
  const defaultThumbnail = getDefaultThumbnailUrl(item as any)

  if (defaultThumbnail && img.src !== defaultThumbnail) {
    img.src = defaultThumbnail
  }
}

function removeItem(item: SelectedFile | SelectedPage) {
  if (isDragNDropEnabled.value) {
    deleteItem(item)
  } else {
    deselectItem(item)
  }
}

function previewFile(item: { id: string; [key: string]: any }, e: MouseEvent) {
  api.fileOpener?.({ fileId: getFileId(item), newTab: isModifierClick(e), pageIndex: item.page?.page_index })
}

const draggingOverItemId = ref<Canvas['id'] | null>(null)
const draggingOverItemSeparator = ref<Canvas['id'] | null>(null)
const isDragging = ref(false)
let dragOverTimeout: ReturnType<typeof setTimeout> | null = null

function clearState() {
  draggingOverItemSeparator.value = null
  draggingOverItemId.value = null
  isDragging.value = false
}

function handleDrop(event: DragEvent, droppedOnItemId?: Canvas['id'], index?: number) {
  event.preventDefault()
  addItems(index ?? 'bottom')
  clearState()
}

function handleDragOver(event: DragEvent, droppedOnItemId?: Canvas['id'], isSeparator?: boolean) {
  event.preventDefault()
  isDragging.value = true

  if (isSeparator) {
    draggingOverItemId.value = null
    draggingOverItemSeparator.value = droppedOnItemId ?? null
  } else {
    draggingOverItemId.value = droppedOnItemId ?? null
    draggingOverItemSeparator.value = null
  }

  if (dragOverTimeout) {
    clearTimeout(dragOverTimeout)
  }

  // Set a new timeout to clear draggingOverItemId after some inactivity
  dragOverTimeout = setTimeout(() => {
    if (!isDragging.value) {
      clearState()
    }
  }, 250)
}

function handleDragEnter(event: DragEvent) {
  event.preventDefault()
  isDragging.value = true
}

function handleDragLeave(event: DragEvent) {
  const relatedTarget = event.relatedTarget as Node
  if (!(event.currentTarget as Node)?.contains(relatedTarget)) {
    isDragging.value = false
    clearState()
  }
}

onUnmounted(() => {
  if (dragOverTimeout) {
    clearTimeout(dragOverTimeout)
  }
})
</script>

<style scoped>
.panel-title {
  color: v-bind('themeVars.text');
  letter-spacing: 0.15px;
}

.base {
  background-color: v-bind('themeVars.base');
}

.selection-panel-list-item {
  background: v-bind('themeVars.primary6');
}

.selection-panel-list-handle {
  color: v-bind('themeVars.primary3');
}

.title {
  color: v-bind('themeVars.text');
  letter-spacing: 0.4px;
}

.description {
  color: v-bind('themeVars.primary3');
}
</style>
